import {  t  } from "@/client/libs/dummy-i18n";
import { PlusIcon, UsersIcon } from "@phosphor-icons/react";
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, Input, Label, Separator } from "@stackmorph/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router";

import { createOrganization, getOrganizations } from "@/client/services/organization/organization";

export const TeamPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations"],
    queryFn: getOrganizations,
  });

  const { mutate: createOrg, isPending } = useMutation({
    mutationFn: createOrganization,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setIsOpen(false);
      setName("");
    },
  });

  const handleCreate = () => {
    if (!name) return;
    createOrg(name);
  };

  return (
    <>
      <Helmet>
        <title>{t`Team`} - {t`Stackmorph Resume Builder`}</title>
      </Helmet>

      <div className="max-w-screen-lg space-y-6 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t`Team`}</h1>
            <p className="text-muted-foreground">
              {t`Manage your organization and team members.`}
            </p>
          </div>

          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
              <Button>
                <PlusIcon className="mr-2" />
                {t`Create Organization`}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t`Create Organization`}</DialogTitle>
                <DialogDescription>
                  {t`Create a new organization to collaborate with your team.`}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">{t`Organization Name`}</Label>
                  <Input
                    id="name"
                    value={name}
                    placeholder="Acme Inc."
                    onChange={(e) => { setName(e.target.value); }}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="ghost" onClick={() => { setIsOpen(false); }}>
                  {t`Cancel`}
                </Button>
                <Button disabled={isPending || !name} onClick={handleCreate}>
                  {t`Create`}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Separator />

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {organizations.length === 0 && (
            <div className="col-span-full py-12 text-center opacity-50">
              <UsersIcon size={48} className="mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium">{t`No organizations found`}</h3>
              <p>{t`Create an organization to get started.`}</p>
            </div>
          )}

          {organizations.map((org) => (
            <div
              key={org.id}
              role="button"
              tabIndex={0}
              className="cursor-pointer outline-none"
              onClick={() => {
                void navigate(`/dashboard/team/${org.id}`);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  void navigate(`/dashboard/team/${org.id}`);
                }
              }}
            >
              <Card className="transition-all hover:shadow-md hover:border-primary/50 h-full">
                <CardHeader>
                  <CardTitle>{org.name}</CardTitle>
                  <CardDescription>{t`Created on`} {new Date(org.createdAt).toLocaleDateString()}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between text-sm">
                    <span>{org._count.users} {t`members`}</span>
                    <span>{org._count.resumes} {t`resumes`}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
